import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber as isValidInternationalNumber,
  type CountryCode,
} from 'libphonenumber-js';

/** Matches the `defaultCountry` of the previous tel input. */
export const DEFAULT_PHONE_COUNTRY: CountryCode = 'US';

export interface PhoneCountry {
  code: CountryCode;
  name: string;
  callingCode: string;
  flag: string;
}

const regionNames = new Intl.DisplayNames(['en'], {type: 'region'});

function toFlagEmoji(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, character =>
      String.fromCodePoint(127397 + character.charCodeAt(0))
    );
}

export const PHONE_COUNTRIES: PhoneCountry[] = getCountries()
  .map(code => ({
    code,
    name: regionNames.of(code) ?? code,
    callingCode: `+${getCountryCallingCode(code)}`,
    flag: toFlagEmoji(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const countriesByCode = new Map<CountryCode, PhoneCountry>(
  PHONE_COUNTRIES.map(country => [country.code, country])
);

export function getPhoneCountry(code: CountryCode): PhoneCountry | undefined {
  return countriesByCode.get(code);
}

/** International format only: a leading `+` is required, as with the previous tel input. */
export function isValidPhoneNumber(input: string | undefined | null): boolean {
  const value = (input ?? '').trim();
  if (value.startsWith('+') === false) {
    return false;
  }

  return isValidInternationalNumber(value);
}

/**
 * Preserves the stored value shape of the previous tel input: an
 * `AsYouType`-formatted international number (e.g. `+1 213 373 4253`).
 */
export function formatPhoneNumberAsYouType(
  input: string,
  country: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  if (input.trim() === '') {
    return '';
  }

  if (input.startsWith('+')) {
    return new AsYouType().input(input);
  }

  const asYouType = new AsYouType(country);
  asYouType.input(input);

  return asYouType.getNumber()?.formatInternational() ?? input;
}

/** The country a value already carries; undefined while the prefix is still ambiguous. */
export function detectPhoneNumberCountry(
  input: string | undefined | null
): CountryCode | undefined {
  const value = (input ?? '').trim();
  if (value.startsWith('+') === false) {
    return undefined;
  }

  const asYouType = new AsYouType();
  asYouType.input(value);

  return asYouType.getCountry();
}

function getNationalDigits(input: string): string {
  const value = input.trim();
  if (value === '') {
    return '';
  }

  if (value.startsWith('+') === false) {
    return value.replace(/\D/g, '');
  }

  const asYouType = new AsYouType();
  asYouType.input(value);
  const number = asYouType.getNumber();

  return number ? String(number.nationalNumber) : '';
}

/** Re-prefixes the digits typed so far with another country's calling code. */
export function applyPhoneNumberCountry(
  input: string | undefined | null,
  country: CountryCode
): string {
  const digits = getNationalDigits(input ?? '');

  return formatPhoneNumberAsYouType(
    `+${getCountryCallingCode(country)}${digits}`,
    country
  );
}
